import type { Collector, Signal } from './types';
import { prop, safe, signal, sorted, unavailable } from './util';

interface Brand {
  brand: string;
  version: string;
}
interface UADataLike {
  brands?: Brand[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
}

const CHROMIUM_ONLY = 'Not supported (Chromium-only API)';
const brandList = (brands: Brand[] | undefined) => sorted((brands ?? []).map((b) => `${b.brand} ${b.version}`));

export const navigatorCollector: Collector = {
  id: 'navigator',
  title: 'Navigator & browser',
  description: 'What your browser says about itself: its name and version, your OS, and a few built-in flags.',
  async collect() {
    if (typeof navigator === 'undefined') return [unavailable('nav.userAgent', 'User agent', 'navigator is not available')];
    const nav = navigator;
    const uaData = prop<UADataLike>(nav, 'userAgentData');

    const probes: Array<Promise<Signal>> = [
      safe('nav.userAgent', 'User agent', () =>
        typeof nav.userAgent === 'string'
          ? signal('nav.userAgent', 'User agent', nav.userAgent)
          : unavailable('nav.userAgent', 'User agent', 'Not exposed'),
      ),

      safe('nav.uaData', 'Client Hints (basic)', () => {
        if (!uaData) return unavailable('nav.uaData', 'Client Hints (basic)', CHROMIUM_ONLY);
        const value = { brands: brandList(uaData.brands), mobile: !!uaData.mobile, platform: uaData.platform ?? '' };
        return signal('nav.uaData', 'Client Hints (basic)', value, {
          display: `${value.brands.join(' · ')}\nmobile: ${value.mobile} · platform: ${value.platform || '(empty)'}`,
        });
      }),

      safe('nav.uaDataHigh', 'Client Hints (high entropy)', async () => {
        if (!uaData?.getHighEntropyValues) return unavailable('nav.uaDataHigh', 'Client Hints (high entropy)', CHROMIUM_ONLY);
        const v = await uaData.getHighEntropyValues([
          'architecture',
          'bitness',
          'model',
          'platformVersion',
          'fullVersionList',
          'wow64',
        ]);
        const value = {
          architecture: String(v.architecture ?? ''),
          bitness: String(v.bitness ?? ''),
          model: String(v.model ?? ''),
          platformVersion: String(v.platformVersion ?? ''),
          fullVersionList: brandList(v.fullVersionList as Brand[] | undefined),
          wow64: Boolean(v.wow64),
        };
        const display = [
          `full versions: ${value.fullVersionList.join(' · ') || '(none)'}`,
          `platform version: ${value.platformVersion || '(empty)'}`,
          `architecture: ${value.architecture || '(empty)'} · bitness: ${value.bitness || '(empty)'}`,
          `model: ${value.model || '(empty)'}`,
        ].join('\n');
        return signal('nav.uaDataHigh', 'Client Hints (high entropy)', value, {
          display,
          note: 'Readable by any script without a permission prompt.',
        });
      }),

      safe('nav.platform', 'Platform', () => signal('nav.platform', 'Platform', nav.platform ?? '')),
      safe('nav.vendor', 'Vendor', () => signal('nav.vendor', 'Vendor', nav.vendor ?? '')),

      safe('nav.product', 'Product / build', () => {
        const value = { product: nav.product ?? '', productSub: prop<string>(nav, 'productSub') ?? '' };
        return signal('nav.product', 'Product / build', value, { display: `${value.product} / ${value.productSub}` });
      }),

      safe('nav.webdriver', 'Automation flag (webdriver)', () =>
        signal('nav.webdriver', 'Automation flag (webdriver)', !!nav.webdriver, {
          note: nav.webdriver ? 'This browser reports it is controlled by automation.' : undefined,
        }),
      ),

      safe('nav.pdfViewerEnabled', 'Built-in PDF viewer', () => {
        const v = prop<boolean>(nav, 'pdfViewerEnabled');
        return typeof v === 'boolean'
          ? signal('nav.pdfViewerEnabled', 'Built-in PDF viewer', v)
          : unavailable('nav.pdfViewerEnabled', 'Built-in PDF viewer', 'Not supported in this browser');
      }),

      safe('nav.plugins', 'Plugins', () => {
        const names = Array.from(nav.plugins ?? [], (p) => p.name);
        return signal('nav.plugins', 'Plugins', names, {
          display: names.length ? `${names.length}: ${names.join(', ')}` : '0 (none)',
          note: 'Modern browsers report a fixed, frozen list here, so it says little beyond the browser family.',
        });
      }),

      safe('nav.mimeTypes', 'Plugin MIME types', () => {
        const types = sorted(Array.from(nav.mimeTypes ?? [], (m) => m.type));
        return signal('nav.mimeTypes', 'Plugin MIME types', types, {
          display: types.length ? `${types.length}: ${types.join(', ')}` : '0 (none)',
        });
      }),

      safe('nav.vendorGlobals', 'Browser-specific objects', async () => {
        const w = window as unknown as Record<string, unknown>;
        const checks: Array<[string, boolean]> = [
          ['window.chrome', typeof w.chrome === 'object' && w.chrome !== null],
          ['navigator.brave', typeof prop(nav, 'brave') === 'object'],
          ['window.safari', typeof w.safari === 'object' && w.safari !== null],
          ['window.opr', typeof w.opr === 'object' && w.opr !== null],
          ['window.mozInnerScreenX', 'mozInnerScreenX' in w],
          ['window.__gCrWeb', '__gCrWeb' in w],
          ['window.__firefox__', '__firefox__' in w],
          ['navigator.standalone', 'standalone' in nav],
        ];
        const present = checks.filter(([, ok]) => ok).map(([name]) => name);
        let brave = false;
        const braveApi = prop<{ isBrave?: () => Promise<boolean> }>(nav, 'brave');
        if (braveApi?.isBrave) brave = await braveApi.isBrave().catch(() => false);
        return signal('nav.vendorGlobals', 'Browser-specific objects', { present, brave }, {
          display: `${present.length ? present.join(', ') : '(none of the checked objects)'}${brave ? '\nnavigator.brave.isBrave() → true' : ''}`,
        });
      }),

      safe('nav.doNotTrack', 'Do Not Track', () => {
        const raw = nav.doNotTrack ?? prop<string>(window, 'doNotTrack') ?? prop<string>(nav, 'msDoNotTrack');
        if (raw === undefined) return unavailable('nav.doNotTrack', 'Do Not Track', 'Not supported (removed in Safari)');
        const value = raw === null ? 'unset' : String(raw);
        const display = value === '1' ? '1 (enabled)' : value === '0' ? '0 (disabled)' : value;
        return signal('nav.doNotTrack', 'Do Not Track', value, { display });
      }),

      safe('nav.globalPrivacyControl', 'Global Privacy Control', () => {
        const v = prop<boolean>(nav, 'globalPrivacyControl');
        return typeof v === 'boolean'
          ? signal('nav.globalPrivacyControl', 'Global Privacy Control', v, { display: v ? 'true (enabled)' : 'false' })
          : unavailable('nav.globalPrivacyControl', 'Global Privacy Control', 'Not supported in this browser');
      }),

      safe('nav.cookieEnabled', 'Cookies enabled', () => signal('nav.cookieEnabled', 'Cookies enabled', !!nav.cookieEnabled)),
    ];
    return Promise.all(probes);
  },
};
